/**
 * Server functions for the Dutch Cozumel guide.
 *
 * - Admin fns are role-gated (owner/admin) and manage public links.
 * - `openCozumelGuideLink` is public (no auth): the token IS the credential.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/lib/require-auth";
import { userHasAnyRole } from "@/lib/has-role.server";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import type { CozumelLinkRow, PublicLinkStatus } from "@/lib/cozumel-nl-guide.server";

async function assertAdmin(ctx: { supabase: SupabaseClient<Database>; userId: string }) {
  const ok = await userHasAnyRole(ctx.supabase, ctx.userId, ["admin", "owner"]);
  if (!ok) throw new Error("Forbidden");
}

const TokenSchema = z
  .string()
  .trim()
  .regex(/^[a-f0-9]{32}$/, "Token inválido");

export const listCozumelGuideLinks = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ rows: CozumelLinkRow[] }> => {
    await assertAdmin(context as never);
    const { listLinks } = await import("@/lib/cozumel-nl-guide.server");
    return { rows: await listLinks() };
  });

export const createCozumelGuideLink = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        label: z.string().trim().max(120).optional(),
        customerName: z.string().trim().max(120).optional(),
        notes: z.string().trim().max(500).optional(),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data, context }): Promise<{ row: CozumelLinkRow }> => {
    await assertAdmin(context as never);
    const ctx = context as unknown as { userId: string };
    const { createLink } = await import("@/lib/cozumel-nl-guide.server");
    const row = await createLink({
      label: data.label ?? null,
      customerName: data.customerName ?? null,
      notes: data.notes ?? null,
      createdBy: ctx.userId,
    });
    return { row };
  });

export const setCozumelGuideLinkRevoked = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ id: z.string().uuid(), revoked: z.boolean() }).parse(d),
  )
  .handler(async ({ data, context }): Promise<{ row: CozumelLinkRow }> => {
    await assertAdmin(context as never);
    const { setLinkRevoked } = await import("@/lib/cozumel-nl-guide.server");
    return { row: await setLinkRevoked(data.id, data.revoked) };
  });

/** Public: validates the token, registers the visit and reports download state. */
export const openCozumelGuideLink = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ token: TokenSchema }).parse(d))
  .handler(async ({ data }): Promise<PublicLinkStatus> => {
    const { openLink } = await import("@/lib/cozumel-nl-guide.server");
    return openLink(data.token);
  });
